import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TaxYearState {
  /** Default year from server (set after config fetch) */
  defaultYear: number | null;
  /** Currently selected year for docs/tax filings (user selection) */
  selectedYear: number | null;
  /** Supported tax years from the server (newest first) */
  availableYears: number[];
  /** Whether config has been fetched this session */
  configFetched: boolean;
}

interface TaxYearActions {
  /** Apply server (or fallback) config; reconcile persisted selection */
  applyConfig: (supportedYears: number[], defaultTaxYear: number) => void;
  setSelectedYear: (year: number) => void;
  setConfigFetched: (fetched: boolean) => void;
  /** Effective year for API calls (selected or default) */
  getEffectiveYear: () => number | null;
}

/** Newest years first for the dropdown */
function sortYearsDescending(years: number[]): number[] {
  return [...years].sort((a, b) => b - a);
}

export const useTaxYearStore = create<TaxYearState & TaxYearActions>()(
  persist(
    (set, get) => ({
      defaultYear: null,
      selectedYear: null,
      availableYears: [],
      configFetched: false,

      applyConfig: (supportedYears, defaultTaxYear) => {
        const years = sortYearsDescending(supportedYears);
        const stored = get().selectedYear;
        const useStored =
          stored !== null && Number.isInteger(stored) && years.includes(stored);
        const yearToUse = useStored ? stored : defaultTaxYear;
        set({
          defaultYear: defaultTaxYear,
          availableYears: years,
          selectedYear: yearToUse,
          configFetched: true,
        });
      },

      setSelectedYear: (year) => {
        const { availableYears } = get();
        if (!Number.isInteger(year)) return;
        if (availableYears.length > 0 && !availableYears.includes(year)) return;
        set({ selectedYear: year });
      },

      setConfigFetched: (fetched) => set({ configFetched: fetched }),

      getEffectiveYear: () => {
        const s = get();
        return s.selectedYear ?? s.defaultYear ?? null;
      },
    }),
    {
      name: 'tax-year-store',
      partialize: (state) => ({ selectedYear: state.selectedYear }),
    }
  )
);
